/**
 * Pure resolution of the portal's runtime configuration.
 *
 * The same static build is meant to run in every environment, so nothing in
 * here may read `process.env` or `window` directly. Every endpoint is derived
 * from the hostname the portal is served on, following the CloudCharge naming
 * convention:
 *
 *   <env>.portal.cloudcharge.se  ->  <env>.graphql.cloudcharge.se  (graph, prime, subscription)
 *                                    <env>.cloudcharge.se          (kaiser, core services)
 *   portal.cloudcharge.se        ->  graphql.cloudcharge.se
 *                                    prod.cloudcharge.se
 *   localhost                    ->  dev (unless overridden)
 *
 * Anything the convention cannot express (a branch preview pointed at dev,
 * per-environment API keys) is supplied through `overrides`.
 * See `./index.ts` for where those come from at runtime.
 */

export interface PortalConfig {
    /** Deployment environment the backends belong to: 'dev', 'staging', 'demo', 'production', … */
    environment: string;
    /** True when the portal itself is served from localhost / 127.0.0.1. */
    isLocalhost: boolean;
    /** cc-graph-api GraphQL endpoint, e.g. https://dev.graphql.cloudcharge.se/graphql */
    graphUrl: string;
    /** Admin Prime REST base, e.g. https://dev.graphql.cloudcharge.se/prime */
    adminPrimeUrl: string;
    /** Subscription service REST base, e.g. https://dev.graphql.cloudcharge.se/subscription */
    subscriptionServiceUrl: string;
    /** Kaiser host only (operation paths in the spec include the base path), e.g. https://dev.cloudcharge.se */
    kaiserBaseUrl: string;
    /** Core services base, e.g. https://dev.cloudcharge.se/services */
    coreServicesUrl: string;
    /** Split.io browser SDK key. Differs per environment. */
    splitioApiKey: string;
    /** Google Maps browser key. */
    googleMapsApiKey: string;
    /** Build identity (release tag). Baked in at build time; not an environment property. */
    version?: string;
}

/**
 * Subset of the config that can be supplied from outside. Endpoint overrides
 * are taken verbatim; `graphUrl` alone is enough to also derive prime and
 * subscription (they share its origin) unless those are given explicitly.
 */
export type PortalConfigOverrides = Partial<Omit<PortalConfig, 'isLocalhost'>>;

const PRODUCTION = 'production';
const DEFAULT_LOCALHOST_ENVIRONMENT = 'dev';
const CORE_DOMAIN = 'cloudcharge.se';

export function isLocalhostHostname(hostname: string): boolean {
    return (
        /^(localhost|127\.0\.0\.1|\[::1\]|::1)$/i.test(hostname) || hostname.endsWith('.localhost')
    );
}

/**
 * Environment name from a hostname: the first label, except that a hostname
 * starting with `portal.`/`graphql.` (no env prefix) is production.
 */
export function environmentFromHostname(hostname: string): string {
    const [first] = hostname.toLowerCase().split('.');
    if (first === 'portal' || first === 'graphql' || first === 'prod') {
        return PRODUCTION;
    }
    return first;
}

function environmentFromUrl(url: string): string | undefined {
    try {
        const { hostname } = new URL(url);
        return isLocalhostHostname(hostname) ? undefined : environmentFromHostname(hostname);
    } catch {
        return undefined;
    }
}

/** Host for kaiser / core services: `<env>.cloudcharge.se`, production is `prod.cloudcharge.se`. */
function coreHost(environment: string): string {
    return environment === PRODUCTION ? `prod.${CORE_DOMAIN}` : `${environment}.${CORE_DOMAIN}`;
}

/** Origin for cc-graph-api, derived from the portal's own origin by convention. */
function graphOrigin(location: URL, environment: string): string {
    const host = location.host.toLowerCase();
    if (isLocalhostHostname(location.hostname)) {
        return environment === PRODUCTION
            ? `https://graphql.${CORE_DOMAIN}`
            : `https://${environment}.graphql.${CORE_DOMAIN}`;
    }
    if (host.includes('portal')) {
        // dev.portal.cloudcharge.se -> dev.graphql.cloudcharge.se; also keeps
        // working for hosts outside cloudcharge.se that follow the same pattern.
        return `${location.protocol}//${host.replace('portal', 'graphql')}`;
    }
    return environment === PRODUCTION
        ? `https://graphql.${CORE_DOMAIN}`
        : `https://${environment}.graphql.${CORE_DOMAIN}`;
}

function trimTrailingSlash(url: string): string {
    return url.replace(/\/+$/, '');
}

/** Replace the path of `url` with `path`, dropping query and hash. */
function withPath(url: string, path: string): string {
    const parsed = new URL(url);
    parsed.pathname = path;
    parsed.search = '';
    parsed.hash = '';
    return trimTrailingSlash(parsed.toString());
}

export function resolveConfig(
    location: URL | string,
    overrides: PortalConfigOverrides = {}
): PortalConfig {
    const url = typeof location === 'string' ? new URL(location) : location;
    const isLocalhost = isLocalhostHostname(url.hostname);

    const environment =
        overrides.environment ??
        (isLocalhost
            ? // On localhost, follow whichever backend the graph URL points at so
              // kaiser/core end up in the same environment as cc-graph-api.
              (overrides.graphUrl && environmentFromUrl(overrides.graphUrl)) ||
              DEFAULT_LOCALHOST_ENVIRONMENT
            : environmentFromHostname(url.hostname));

    const graphUrl = overrides.graphUrl
        ? trimTrailingSlash(overrides.graphUrl)
        : `${graphOrigin(url, environment)}/graphql`;

    const adminPrimeUrl = overrides.adminPrimeUrl
        ? trimTrailingSlash(overrides.adminPrimeUrl)
        : withPath(graphUrl, '/prime');
    const subscriptionServiceUrl = overrides.subscriptionServiceUrl
        ? trimTrailingSlash(overrides.subscriptionServiceUrl)
        : withPath(graphUrl, '/subscription');

    const kaiserBaseUrl = overrides.kaiserBaseUrl
        ? trimTrailingSlash(overrides.kaiserBaseUrl)
        : `https://${coreHost(environment)}`;
    const coreServicesUrl = overrides.coreServicesUrl
        ? trimTrailingSlash(overrides.coreServicesUrl)
        : `https://${coreHost(environment)}/services`;

    return {
        environment,
        isLocalhost,
        graphUrl,
        adminPrimeUrl,
        subscriptionServiceUrl,
        kaiserBaseUrl,
        coreServicesUrl,
        splitioApiKey: overrides.splitioApiKey ?? '',
        googleMapsApiKey: overrides.googleMapsApiKey ?? '',
        version: overrides.version || undefined,
    };
}
